import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthState } from '../hooks/useAuthState';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db, storage } from '../firebase/config';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getUserGroups, getPublicGroups, getGroup } from '../lib/firestore/groups';
import { getEventsByHost, getUpcomingEvents, getPublicEvents, getDiscoverableEvents } from '../lib/firestore/events';
import { getRandomMotivationalMessage, getRandomEventPrompt } from '../lib/messages';
import { formatEventDateTime } from '../lib/date-utils';
import NotificationBell from '../components/NotificationBell';
import '../styles/Home.css';

// EventCard component for Partiful-style cards
const EventCard = ({ event, locationDisplay, onClick }) => {
  const [hostName, setHostName] = useState('');
  const [loadingHost, setLoadingHost] = useState(true);

  useEffect(() => {
    const loadHostName = async () => {
      try {
        if (event.hostType === 'group') {
          const group = await getGroup(event.hostId);
          setHostName(group?.name || 'Unknown Group');
        } else {
          const userDoc = await getDoc(doc(db, 'users', event.hostId));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            const firstName = userData.firstName || '';
            const lastName = userData.lastName || '';
            setHostName(firstName && lastName ? `${firstName} ${lastName}` : firstName || lastName || 'Unknown User');
          } else {
            setHostName('Unknown User');
          }
        }
      } catch (error) {
        console.error('Error loading host name:', error);
        setHostName('Unknown');
      } finally {
        setLoadingHost(false);
      }
    };

    if (event) {
      loadHostName();
    }
  }, [event]);

  const getEventCardColor = (id) => {
    const colors = [
      { accent: '#00d9ff' }, // Teal
      { accent: '#ff6b35' }, // Orange
      { accent: '#00ffb8' }, // Teal-green
      { accent: '#ffaa00' }, // Orange-yellow
      { accent: '#00b8d4' }, // Darker teal
      { accent: '#ff8c42' }, // Lighter orange
    ];
    const hash = id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[hash % colors.length];
  };

  const accentColor = getEventCardColor(event.id).accent;

  return (
    <div
      className="home-event-card"
      onClick={onClick}
      style={{
        '--event-accent-color': accentColor,
      }}
    >
      <div className="home-event-image-wrapper">
        {event.image ? (
          <img src={event.image} alt={event.title} className="home-event-image" />
        ) : (
          <div className="home-event-avatar">
            {event.title ? (
              <span>{event.title.charAt(0).toUpperCase()}</span>
            ) : (
              <span>🎉</span>
            )}
          </div>
        )}
        
        {/* Title bubble overlay at top-left */}
        <div className="home-event-title-bubble">
          <span className="home-event-title-text">{event.title}</span>
        </div>
        
        {/* Hosted by and location overlay at bottom */}
        <div className="home-event-bottom-overlay">
          {event.description && (
            <div className="home-event-description-overlay">
              {event.description}
            </div>
          )}
          <div className="home-event-hosted-by">
            {loadingHost ? 'Loading...' : `Hosted by ${hostName}`}
          </div>
          <div className="home-event-location-overlay">
            {locationDisplay}
          </div>
        </div>
      </div>
    </div>
  );
};

const Home = () => {
  const { user, loading: authLoading } = useAuthState();
  const navigate = useNavigate();
  const location = useLocation();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingName, setEditingName] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [groups, setGroups] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [activeTab, setActiveTab] = useState('upcoming');
  const [pastEvents, setPastEvents] = useState([]);
  const [motivationalMessage, setMotivationalMessage] = useState('');
  const [eventPrompt, setEventPrompt] = useState('');
  const [hostingEvents, setHostingEvents] = useState([]);
  const [invites, setInvites] = useState([]);
  const [publicItems, setPublicItems] = useState([]); // Combined public groups and events
  const [loadingPublic, setLoadingPublic] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const [showCreateDropdown, setShowCreateDropdown] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const createDropdownRef = useRef(null);
  const profileDropdownRef = useRef(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is on a dropdown item - if so, don't close (let the item's handler do it)
      const isDropdownItem = event.target.closest('.create-dropdown-item') || 
                             event.target.closest('.profile-dropdown-item');
      if (isDropdownItem) {
        return; // Let the dropdown item's onClick handle it
      }

      if (createDropdownRef.current && !createDropdownRef.current.contains(event.target)) {
        setShowCreateDropdown(false);
      }
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target)) {
        setShowProfileDropdown(false);
      }
    };

    if (showCreateDropdown || showProfileDropdown) {
      // Use 'click' instead of 'mousedown' to allow dropdown item clicks to fire first
      document.addEventListener('click', handleClickOutside);
      return () => {
        document.removeEventListener('click', handleClickOutside);
      };
    }
  }, [showCreateDropdown, showProfileDropdown]);


  useEffect(() => {
    // Only run if we're actually on the home route
    if (location.pathname !== '/home' && location.pathname !== '/') {
      console.log('🔵 [Home] Not on home route, skipping load:', location.pathname);
      return;
    }

    if (!authLoading) {
      if (!user) {
        navigate('/login');
        return;
      }
      console.log('🔵 [Home] Loading data for home page');
      loadUserData();
      loadLogo();
      loadGroupsAndEvents().then(() => {
        // Load public items after user's groups/events are loaded
        loadPublicItems();
      });
      // Set random messages on load
      setMotivationalMessage(getRandomMotivationalMessage());
      setEventPrompt(getRandomEventPrompt());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading, location.pathname]);

  const loadLogo = async () => {
    try {
      // Try to load logo from Firebase Storage (PNG format with transparency)
      const logoRef = ref(storage, 'hotelia-logo.png');
      const url = await getDownloadURL(logoRef);
      setLogoUrl(url);
      console.log('✅ Logo loaded from Firebase Storage');
    } catch (error) {
      // If logo doesn't exist in Storage yet, we'll show a placeholder
      console.log('⚠️ Logo not found in Storage. Upload it to Firebase Storage at: hotelia-logo.png');
      console.log('   Error:', error.message);
      setLogoUrl(null);
    }
  };


  const loadUserData = async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists()) {
        const data = userDoc.data();
        setUserData(data);
        setFirstName(data.firstName || '');
        setLastName(data.lastName || '');
      } else {
        // User document doesn't exist yet - set empty userData
        setUserData({});
      }
      setLoading(false);
    } catch (error) {
      console.error('Error loading user data:', error);
      setLoading(false);
    }
  };

  const handleProfilePictureClick = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (file) {
        await uploadProfilePicture(file);
      }
    };
    input.click();
  };

  const uploadProfilePicture = async (file) => {
    if (!user) return;

    setUploadingPhoto(true);
    try {
      const fileRef = ref(storage, `profile-pictures/${user.uid}`);
      await uploadBytes(fileRef, file);
      const downloadURL = await getDownloadURL(fileRef);

      await updateDoc(doc(db, 'users', user.uid), {
        profilePicture: downloadURL,
      });

      // Update state immediately with the new URL
      setUserData(prev => ({
        ...prev,
        profilePicture: downloadURL
      }));
      
      // Also reload user data to ensure consistency
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists()) {
        const updatedData = userDoc.data();
        setUserData(updatedData);
      }
    } catch (error) {
      console.error('Error uploading photo:', error);
      alert('Failed to upload photo. Please try again.');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleNameClick = () => {
    setEditingName(true);
  };

  const handleProfileClick = () => {
    setShowProfileDropdown(!showProfileDropdown);
  };

  const handleEditNameFromDropdown = () => {
    setShowProfileDropdown(false);
    setEditingName(true);
  };

  const handleUploadPhotoFromDropdown = () => {
    setShowProfileDropdown(false);
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = 'image/*';
    fileInput.onchange = (e) => {
      const file = e.target.files[0];
      if (file) {
        uploadProfilePicture(file);
      }
    };
    fileInput.click();
  };

  const handleNameSave = async () => {
    if (!user) return;

    try {
      await updateDoc(doc(db, 'users', user.uid), {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
      });

      setUserData({ ...userData, firstName: firstName.trim(), lastName: lastName.trim() });
      setEditingName(false);
    } catch (error) {
      console.error('Error updating name:', error);
      alert('Failed to update name. Please try again.');
    }
  };

  const handleNameCancel = () => {
    setFirstName(userData?.firstName || '');
    setLastName(userData?.lastName || '');
    setEditingName(false);
  };


  const loadGroupsAndEvents = async () => {
    if (!user) return;

    setLoadingGroups(true);
    try {
      console.log('🔵 [Home] Loading groups and events...');
      // Load user's groups
      const userGroups = await getUserGroups(user.uid);
      console.log('🔵 [Home] Loaded groups:', userGroups);
      console.log('🔵 [Home] Groups count:', userGroups.length);
      setGroups(userGroups);

      // Load upcoming events (from profile and groups)
      const profileEvents = await getEventsByHost('profile', user.uid);
      const allGroupEvents = [];
      
      for (const group of userGroups) {
        const groupEvents = await getEventsByHost('group', group.id);
        allGroupEvents.push(...groupEvents);
      }

      // Combine and sort by date
      const allEvents = [...profileEvents, ...allGroupEvents];
      const now = new Date();
      const upcoming = allEvents
        .filter((e) => new Date(e.startsAt) >= now)
        .sort((a, b) => new Date(a.startsAt) - new Date(b.startsAt))
        .slice(0, 10);
      
      setUpcomingEvents(upcoming);

      // Past events (your events that have already happened, most recent first)
      const past = allEvents
        .filter((e) => new Date(e.startsAt) < now)
        .sort((a, b) => new Date(b.startsAt) - new Date(a.startsAt));
      setPastEvents(past);
      
      // Set hosting events (events where user is host) – include all for tab filtering
      const hosting = allEvents.filter((e) => e.hostId === user.uid);
      setHostingEvents(hosting);
      
      // TODO: Load invites from invites collection
      // For now, set empty array
      setInvites([]);
    } catch (error) {
      console.error('Error loading groups and events:', error);
    } finally {
      setLoadingGroups(false);
    }
  };

  const loadPublicItems = async () => {
    if (!user) return;

    setLoadingPublic(true);
    try {
      console.log('🔵 [Home] Loading public groups and events...');
      
      // Get current user's groups and events from state (they should be loaded by now)
      const userGroupIds = groups.map(g => g.id);
      const userEventIds = new Set([
        ...upcomingEvents.map(e => e.id),
        ...hostingEvents.map(e => e.id)
      ]);

      console.log('🔵 [Home] User group IDs:', userGroupIds);
      console.log('🔵 [Home] User event IDs:', Array.from(userEventIds));

      // Load public groups and discoverable events (includes public + group_only for user's groups + event_only for user's events)
      const [publicGroups, discoverableEvents] = await Promise.all([
        getPublicGroups(10),
        getDiscoverableEvents(userGroupIds, 10, Array.from(userEventIds))
      ]);

      console.log('🔵 [Home] Found public groups:', publicGroups.length);
      console.log('🔵 [Home] Found discoverable events:', discoverableEvents.length);

      const filteredGroups = publicGroups
        .filter(g => !userGroupIds.includes(g.id))
        .map(g => ({ ...g, type: 'group' }));
      
      const now = new Date();
      const filteredEvents = discoverableEvents
        .filter(e => !userEventIds.has(e.id) && new Date(e.startsAt) >= now)
        .map(e => ({ ...e, type: 'event' }));

      console.log('🔵 [Home] Filtered groups (after removing user groups):', filteredGroups.length);
      console.log('🔵 [Home] Filtered events (after removing user events):', filteredEvents.length);

      // Combine and randomize
      const combined = [...filteredGroups, ...filteredEvents];
      const shuffled = combined.sort(() => Math.random() - 0.5).slice(0, 12);
      
      console.log('🔵 [Home] Combined items:', combined.length);
      console.log('🔵 [Home] Final shuffled items:', shuffled.length);
      console.log('🔵 [Home] Public items data:', shuffled);
      
      setPublicItems(shuffled);
    } catch (error) {
      console.error('❌ [Home] Error loading public items:', error);
      console.error('❌ [Home] Error details:', error.message, error.stack);
      // Set empty array on error so section doesn't show
      setPublicItems([]);
    } finally {
      setLoadingPublic(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="home-container">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  const displayName = userData?.firstName || userData?.lastName
    ? `${userData.firstName || ''} ${userData.lastName || ''}`.trim()
    : 'Friend';

  // Get events for current tab (upcoming/hosting show only future; past shows only past)
  const getEventsForTab = () => {
    const now = new Date();
    switch (activeTab) {
      case 'hosting':
        return hostingEvents.filter((e) => new Date(e.startsAt) >= now);
      case 'invites':
        return invites;
      case 'past':
        return pastEvents;
      case 'upcoming':
      default:
        return upcomingEvents;
    }
  };

  // Generate a subtle accent color for each event (for borders/accents only)
  const getEventCardColor = (eventId) => {
    const colors = [
      { accent: '#00d9ff' }, // Teal
      { accent: '#ff6b35' }, // Orange
      { accent: '#00ffb8' }, // Teal-green
      { accent: '#ffaa00' }, // Orange-yellow
      { accent: '#00b8d4' }, // Darker teal
      { accent: '#ff8c42' }, // Lighter orange
    ];
    
    // Use event ID to consistently assign a color (so same event always gets same color)
    const hash = eventId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[hash % colors.length];
  };

  return (
    <div className="home-container">
      <header className="home-header" style={{ position: 'relative' }}>
        {/* Desktop: Profile in middle, actions on right */}
        
        {/* Mobile/Tablet: Profile picture and name on left */}
        <div className="profile-section-mobile mobile-only" ref={profileDropdownRef}>
          <div className="profile-picture-wrapper-mobile" onClick={handleProfileClick}>
            {uploadingPhoto ? (
              <div className="profile-picture-loading">⋯</div>
            ) : (
              <>
                {userData?.profilePicture ? (
                  <img
                    src={userData.profilePicture}
                    alt="Profile"
                    className="profile-picture"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                    onLoad={() => {
                      setUploadingPhoto(false);
                    }}
                  />
                ) : (
                  <div className="profile-picture-placeholder">
                    {displayName.charAt(0).toUpperCase()}
                  </div>
                )}
              </>
            )}
          </div>
          <h1 className="profile-name-mobile" onClick={handleProfileClick}>
            {displayName}
          </h1>
          {showProfileDropdown && (
            <div className="profile-dropdown">
              <div className="profile-dropdown-item" onClick={handleEditNameFromDropdown}>
                <div className="profile-dropdown-title">✏️ Edit Name</div>
                <div className="profile-dropdown-description">
                  Change your first and last name
                </div>
              </div>
              <div className="profile-dropdown-item" onClick={handleUploadPhotoFromDropdown}>
                <div className="profile-dropdown-title">📷 Change Photo</div>
                <div className="profile-dropdown-description">
                  Upload a new profile picture
                </div>
              </div>
            </div>
          )}
          {editingName && (
            <div className="name-editor-mobile">
              <input
                type="text"
                className="name-input"
                placeholder="First Name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                autoFocus
              />
              <input
                type="text"
                className="name-input"
                placeholder="Last Name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
              <div className="name-actions">
                <button onClick={handleNameSave} className="btn btn-primary btn-sm">
                  Save
                </button>
                <button onClick={handleNameCancel} className="btn btn-secondary btn-sm">
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Desktop: Profile section in middle */}
        <div className="profile-section desktop-only">
          <div className="profile-picture-wrapper" onClick={handleProfilePictureClick}>
            {uploadingPhoto ? (
              <div className="profile-picture-loading">⋯</div>
            ) : (
              <>
                {userData?.profilePicture ? (
                  <img
                    src={userData.profilePicture}
                    alt="Profile"
                    className="profile-picture"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                    onLoad={() => {
                      setUploadingPhoto(false);
                    }}
                  />
                ) : (
                  <div className="profile-picture-placeholder">
                    {displayName.charAt(0).toUpperCase()}
                  </div>
                )}
              </>
            )}
          </div>
          {editingName ? (
            <div className="name-editor">
              <input
                type="text"
                className="name-input"
                placeholder="First Name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                autoFocus
              />
              <input
                type="text"
                className="name-input"
                placeholder="Last Name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
              <div className="name-actions">
                <button onClick={handleNameSave} className="btn btn-primary btn-sm">
                  Save
                </button>
                <button onClick={handleNameCancel} className="btn btn-secondary btn-sm">
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <h1 className="profile-name" onClick={handleNameClick}>
              {displayName}
            </h1>
          )}
        </div>

            <div className="header-actions">
              {/* Icon-only buttons (same on mobile and desktop) */}
              <div className="header-icons">
                <button
                  className="btn-icon-flat contacts-icon"
                  onClick={() => navigate('/contacts')}
                  title="Contacts"
                >
                  📇
                </button>
                <NotificationBell />
                <button
                  className="btn-icon-flat settings-icon"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    navigate('/settings');
                  }}
                  title="Settings"
                >
                  ⚙️
                </button>
              </div>
            </div>
            
            {/* Floating Create Button (works on both mobile and desktop) */}
            <div className="floating-create-wrapper" ref={createDropdownRef}>
              <button
                className="floating-create-button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowCreateDropdown(!showCreateDropdown);
                }}
                title="Create"
              >
                +
              </button>
              {showCreateDropdown && (
                <>
                  {/* Backdrop to close dropdown */}
                  <div 
                    className="dropdown-backdrop"
                    onClick={() => setShowCreateDropdown(false)}
                  />
                  <div className="create-dropdown floating">
                    <div
                      className="create-dropdown-item"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowCreateDropdown(false);
                        navigate('/create-group');
                      }}
                    >
                      <div className="create-dropdown-title">📁 Create Group</div>
                      <div className="create-dropdown-description">
                        Like-minded people or larger event with sub events. EDC 2026! Anime Fans, Poker group, whatever you named your house!
                      </div>
                    </div>
                    <div
                      className="create-dropdown-item"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowCreateDropdown(false);
                        navigate('/create-event');
                      }}
                    >
                      <div className="create-dropdown-title">🎉 Create Event</div>
                      <div className="create-dropdown-description">
                        Main stage at 9pm, Watching Akira, my girlfriend never saw it. Learn how to pickle pickles. Having dinner, room for more!
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
      </header>

      <main className="home-main">
        {/* Motivational Message Section */}
        <div className="welcome-section">
          <h1 className="welcome-message">
            Hey {displayName} — {motivationalMessage}
          </h1>
          
          <div className="event-prompt-section">
            <p className="event-prompt-label">Don't overthink it! Just create an invite for:</p>
            <p 
              className="event-prompt"
              onClick={() => navigate(`/create-event?title=${encodeURIComponent(eventPrompt)}&description=${encodeURIComponent(eventPrompt)}`)}
              style={{ cursor: 'pointer' }}
            >
              {eventPrompt}
            </p>
          </div>
        </div>

        {/* Groups Section - Horizontal scrolling row */}
        {groups.length > 0 && (
          <section className="groups-section">
            <div className="groups-header">
              <h2>Your Groups</h2>
            </div>
            <div className="groups-scroll-container">
              <div className="groups-row">
                {groups
                  .sort((a, b) => {
                    try {
                      // Sort by recently visited (from localStorage) or updatedAt/createdAt
                      const aVisited = localStorage.getItem(`group_visited_${a.id}`);
                      const bVisited = localStorage.getItem(`group_visited_${b.id}`);
                      
                      let aTime = 0;
                      if (aVisited) {
                        aTime = new Date(aVisited).getTime();
                      } else if (a.updatedAt) {
                        aTime = a.updatedAt.toDate ? a.updatedAt.toDate().getTime() : new Date(a.updatedAt).getTime();
                      } else if (a.createdAt) {
                        aTime = a.createdAt.toDate ? a.createdAt.toDate().getTime() : new Date(a.createdAt).getTime();
                      }
                      
                      let bTime = 0;
                      if (bVisited) {
                        bTime = new Date(bVisited).getTime();
                      } else if (b.updatedAt) {
                        bTime = b.updatedAt.toDate ? b.updatedAt.toDate().getTime() : new Date(b.updatedAt).getTime();
                      } else if (b.createdAt) {
                        bTime = b.createdAt.toDate ? b.createdAt.toDate().getTime() : new Date(b.createdAt).getTime();
                      }
                      
                      return bTime - aTime; // Most recent first
                    } catch (error) {
                      console.error('Error sorting groups:', error);
                      return 0;
                    }
                  })
                  .map((group) => (
                    <div
                      key={group.id}
                      className="group-card-square"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        console.log('🔵 [Home] Clicking group card:', group.id, group.name);
                        console.log('🔵 [Home] Current location:', window.location.pathname);
                        // Track visit
                        localStorage.setItem(`group_visited_${group.id}`, new Date().toISOString());
                        const targetPath = `/groups/${group.id}`;
                        console.log('🔵 [Home] Navigating to:', targetPath);
                        navigate(targetPath);
                        console.log('🔵 [Home] Navigation called, new path should be:', targetPath);
                        // Verify navigation happened
                        setTimeout(() => {
                          console.log('🔵 [Home] After navigation, current path:', window.location.pathname);
                        }, 100);
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="group-card-image">
                        {group.image ? (
                          <img src={group.image} alt={group.name} />
                        ) : (
                          <div className="group-card-avatar">
                            {group.name ? group.name.charAt(0).toUpperCase() : '📁'}
                          </div>
                        )}
                      </div>
                      <div className="group-card-name">{group.name || 'Unnamed Group'}</div>
                    </div>
                  ))}
              </div>
            </div>
          </section>
        )}

        {/* Tabs Navigation */}
        <div className="tabs-container">
          <button
            className={`tab ${activeTab === 'upcoming' ? 'active' : ''}`}
            onClick={() => setActiveTab('upcoming')}
          >
            Upcoming
          </button>
          <button
            className={`tab ${activeTab === 'invites' ? 'active' : ''}`}
            onClick={() => setActiveTab('invites')}
          >
            Invites
          </button>
          <button
            className={`tab ${activeTab === 'hosting' ? 'active' : ''}`}
            onClick={() => setActiveTab('hosting')}
          >
            Hosting
          </button>
          <button
            className={`tab ${activeTab === 'past' ? 'active' : ''}`}
            onClick={() => setActiveTab('past')}
          >
            Past
          </button>
        </div>

        {/* Events Content */}
        <section className="home-section">
          {loadingGroups ? (
            <div className="loading-state">Loading events...</div>
          ) : getEventsForTab().length === 0 ? (
            <div className="empty-state">
              <p>
                {activeTab === 'upcoming' && 'No upcoming events.'}
                {activeTab === 'invites' && 'No invites yet.'}
                {activeTab === 'hosting' && "You're not hosting any events yet."}
                {activeTab === 'past' && 'No past events.'}
              </p>
              {(activeTab === 'upcoming' || activeTab === 'hosting') && (
                <button
                  className="btn btn-primary"
                  onClick={() => navigate('/create-event')}
                >
                  Create Your First Event
                </button>
              )}
            </div>
          ) : (
            <div className="events-grid">
              {getEventsForTab().map((event) => {
                // Extract city/state from location (privacy: only show city/state, not exact address)
                const getLocationDisplay = () => {
                  if (event.location?.city && event.location?.state) {
                    return `${event.location.city}, ${event.location.state}`;
                  }
                  if (event.location?.city) {
                    return event.location.city;
                  }
                  if (event.location?.text) {
                    // Try to extract city/state from text (e.g., "New York, NY" or "123 Main St, New York, NY")
                    const text = event.location.text;
                    const parts = text.split(',').map(p => p.trim());
                    if (parts.length >= 2) {
                      // Assume last two parts are city, state
                      return `${parts[parts.length - 2]}, ${parts[parts.length - 1]}`;
                    }
                    return parts[0] || 'Location TBD';
                  }
                  return 'Location TBD';
                };
                
                const locationDisplay = getLocationDisplay();
                
                return (
                  <EventCard
                    key={event.id}
                    event={event}
                    locationDisplay={locationDisplay}
                    onClick={() => navigate(`/events/${event.id}`)}
                  />
                );
              })}
            </div>
          )}
        </section>

        {/* Public Groups & Events Section - Always show, even if empty */}
        <section className="public-section">
          <div className="section-header">
            <h2>Discover Public Groups & Events</h2>
          </div>
          {loadingPublic ? (
            <div className="loading-state">Loading public groups and events...</div>
          ) : publicItems.length === 0 ? (
            <div className="empty-state">
              <p>No public groups or events available at the moment.</p>
              <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginTop: '0.5rem' }}>
                Create a public group or event to help others discover your community!
              </p>
            </div>
          ) : (
            <div className="public-items-scroll-container">
              <div className="public-items-row">
                {publicItems.map((item) => {
                  const accentColor = getEventCardColor(item.id).accent;
                  
                  // Helper to get location display for events
                  const getLocationDisplay = () => {
                    if (item.type === 'event') {
                      if (item.location?.city && item.location?.state) {
                        return `${item.location.city}, ${item.location.state}`;
                      }
                      if (item.location?.city) {
                        return item.location.city;
                      }
                      if (item.location?.text) {
                        const text = item.location.text;
                        const parts = text.split(',').map(p => p.trim());
                        if (parts.length >= 2) {
                          return `${parts[parts.length - 2]}, ${parts[parts.length - 1]}`;
                        }
                        return parts[0] || 'Location TBD';
                      }
                      return 'Location TBD';
                    }
                    return null;
                  };
                  
                  return (
                    <div
                      key={item.id}
                      className="public-event-wrapper"
                      onClick={() => {
                        if (item.type === 'group') {
                          navigate(`/groups/${item.id}`);
                        } else {
                          navigate(`/events/${item.id}`);
                        }
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      <div
                        className="home-event-card"
                        style={{
                          '--event-accent-color': accentColor,
                        }}
                      >
                        <div className="home-event-image-wrapper">
                          {item.image ? (
                            <img 
                              src={item.image} 
                              alt={item.type === 'group' ? item.name : item.title} 
                              className="home-event-image" 
                            />
                          ) : (
                            <div className="home-event-avatar">
                              {item.type === 'group' ? (
                                item.name ? (
                                  <span>{item.name.charAt(0).toUpperCase()}</span>
                                ) : (
                                  <span>📁</span>
                                )
                              ) : (
                                item.title ? (
                                  <span>{item.title.charAt(0).toUpperCase()}</span>
                                ) : (
                                  <span>🎉</span>
                                )
                              )}
                            </div>
                          )}
                          
                          {/* Title bubble overlay at top-left */}
                          <div className="home-event-title-bubble">
                            <span className="home-event-title-text">
                              {item.type === 'group' ? (item.name || 'Unnamed Group') : item.title}
                            </span>
                          </div>
                          
                          {/* Info overlay at bottom */}
                          <div className="home-event-bottom-overlay">
                            {(item.description || (item.type === 'group' && item.description)) && (
                              <div className="home-event-description-overlay">
                                {item.description}
                              </div>
                            )}
                            <div className="home-event-hosted-by">
                              {item.type === 'group' 
                                ? 'Group' 
                                : (item.hostType === 'group' ? 'Hosted by Group' : 'Hosted by User')}
                            </div>
                            <div className="home-event-location-overlay">
                              {item.type === 'group' 
                                ? (item.visibility === 'public' ? 'Public Group' : 
                                   item.visibility === 'invite_only' ? 'Invite Only' :
                                   item.visibility === 'invite_or_apply' ? 'Apply to Join' : 'Group')
                                : getLocationDisplay()}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </section>
      </main>
      <footer className="app-footer-links" style={{ padding: '1rem 1.5rem', textAlign: 'center', borderTop: '1px solid var(--border-color)', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
        <a href="/privacy.html" style={{ color: 'var(--primary-color)', textDecoration: 'none', marginRight: '1rem' }}>Privacy</a>
        <span style={{ marginRight: '1rem' }}>·</span>
        <a href="/terms.html" style={{ color: 'var(--primary-color)', textDecoration: 'none', marginRight: '1rem' }}>Terms</a>
        <span style={{ marginRight: '1rem' }}>·</span>
        <a href="/sms-consent.html" style={{ color: 'var(--primary-color)', textDecoration: 'none' }}>SMS Consent</a>
      </footer>
    </div>
  );
};

export default Home;
